/**
 * Unity Bridge - React와 Unity WebGL 간 통신을 담당하는 메인 모듈
 *
 * 이 파일은 Unity 통신의 진입점 역할을 하며, 실제 구현은 modules/ 디렉토리의 모듈들로 분리되어 있습니다.
 */

import {
  isUnityReady,
  setUnityReady,
  getUnityInstance,
  resetUnityState,
} from "./modules/unityReadyCheck";
import { unityMessageQueue } from "./modules/unityMessageQueue";
import { SYSTEM_MESSAGE_TYPES } from "../unityConstants";
import { logEditorCorrelation } from "../../../utils/editor";
import {
  getUnityRuntimeBridgeSnapshot,
  isUnityRuntimeReadyAtLeast,
  setUnityRuntimeQueueDepth,
  setUnityRuntimeReadyState,
  type UnityRuntimeReadyState,
  UNITY_RUNTIME_READY_STATE_EVENT,
} from "../../../services/editor/unityRuntimeBridgeState";
import { normalizeUnityIframeMessageData } from "../../../services/editor/spaceNavigationEvents";
import { getActiveSpaceSwitchTransactionId } from "../../../services/editor/spaceSwitchState";
import { getEditorImportStateSnapshot, serializeEditorImportCommand } from "../../../services/editor/editorImportProjection";
import { useUploadStore } from "../../../store/uploadStore";

const isDev = process.env.NODE_ENV === "development";

// 타임아웃 체크를 위한 인터벌 관리
let unityCheckInterval: NodeJS.Timeout | null = null;
let pendingUnityCheck: Promise<HTMLIFrameElement> | null = null;

// 이벤트 리스너 정리를 위한 참조
let messageEventListener: ((event: MessageEvent) => void) | null = null;
let runtimeReadyStateListener: ((event: Event) => void) | null = null;
let isInitialized = false;
let unsubscribeImportProjection: (() => void) | null = null;

function syncEditorImportProjection(): void {
  if (!isEditorPage()) return;
  const snapshot = getEditorImportStateSnapshot();
  void sendToUnityWithResponse("reactToUnity_requestUploadButton", {}, {
    dedupeKey: `editor-import-state:${snapshot.sessionId}:${snapshot.revision}`,
    requiresReadyState: "handlers_ready",
  }).catch(() => undefined);
}

interface UnityMessageEvent {
  type?: string;
  payload?: unknown;
  viewRData?: unknown;
  data?: unknown;
}

type UnitySendRetryPolicy = {
  maxAttempts?: 1 | 2;
  retryOnReadyBoundary?: boolean;
};

export type UnitySendOptions = {
  /** Rechecked after readiness waits and at every actual/retry send boundary. */
  assertCurrent?: () => void;
  transactionId?: string | null;
  dedupeKey?: string;
  requiresReadyState?: UnityRuntimeReadyState;
  retryPolicy?: UnitySendRetryPolicy;
  __skipRuntimeGate?: boolean;
  /** 활성 space 트랜잭션과 무관한 상관 ID(예: editor-import-reload)일 때 stale drop 방지 */
  skipStaleTransactionCheck?: boolean;
};

/** UnityBridge 활성 트랜잭션과 별도로 상관 추적만 하는 ID */
export function isEditorImportReloadCorrelationTransaction(
  transactionId: string | null | undefined,
): boolean {
  return (
    typeof transactionId === "string" &&
    transactionId.startsWith("editor-import-reload:")
  );
}

type RuntimeQueuedCommand = {
  command: string;
  payload: Record<string, unknown>;
  options: UnitySendOptions;
  requiredReadyState: UnityRuntimeReadyState;
  dedupeKey: string;
  transactionId: string | null;
  maxAttempts: number;
  attempts: number;
  waitUntilBoundarySeq: number | null;
  resolve: () => void;
  reject: (reason?: unknown) => void;
};

const RUNTIME_READY_GATED_COMMANDS = new Set<string>([
  "reactToUnity_xrOverlayGazeOff",
  // compat-only: upload button authority belongs to Unity's canonical state
  // (uploadButtonEnabled field in spaceNavigationState). React must NOT call
  // this command with enabled=true to override Unity's authoritative value.
  // The command is kept registered for legacy fallback only (e.g. pre-canonical
  // flows). Any new call sites must pass enabled=false or be guarded by a
  // "no active canonical transaction" check before sending.
  "reactToUnity_setUploadButtonEnabled",
  "reactToUnity_loadViewR",
]);

const RUNTIME_DELIVERED_DEDUPE_COMMANDS = new Set<string>([
  "reactToUnity_xrOverlayGazeOff",
  "reactToUnity_setUploadButtonEnabled",
  "reactToUnity_xrEntryPointGetList",
]);

// 브리지 전역 활성 트랜잭션과 독립된 자체 트랜잭션 ID 생명주기를 가지는 커맨드는
// isStaleQueuedCommandTransaction 비교 대상에서 제외한다.
// (XR overlay gaze shutdown 은 space switch 등 다른 트랜잭션과 무관하게 실행되어야 한다)
const RUNTIME_STALE_CHECK_EXEMPT_COMMANDS = new Set<string>([
  "reactToUnity_xrOverlayGazeOff",
]);

const runtimeCommandQueue: RuntimeQueuedCommand[] = [];
const runtimeQueuedDedupeKeys = new Set<string>();
const runtimeDeliveredDedupeKeys = new Set<string>();
let runtimeQueueFlushInProgress = false;

function getCommandTransactionId(
  payload: Record<string, unknown>,
  options?: UnitySendOptions,
): string | null {
  if (typeof options?.transactionId === "string" && options.transactionId.trim()) {
    return options.transactionId;
  }
  const payloadTransactionId = payload.transactionId;
  return typeof payloadTransactionId === "string" && payloadTransactionId.trim()
    ? payloadTransactionId
    : null;
}

function getRequiredReadyState(
  command: string,
  options?: UnitySendOptions,
): UnityRuntimeReadyState {
  if (options?.requiresReadyState) {
    return options.requiresReadyState;
  }
  return RUNTIME_READY_GATED_COMMANDS.has(command) ? "handlers_ready" : "booting";
}

function getRetryPolicy(command: string, options?: UnitySendOptions): {
  maxAttempts: 1 | 2;
  retryOnReadyBoundary: boolean;
} {
  const defaultMaxAttempts: 1 | 2 = RUNTIME_READY_GATED_COMMANDS.has(command)
    ? 2
    : 1;
  const maxAttempts =
    options?.retryPolicy?.maxAttempts ?? defaultMaxAttempts;
  const retryOnReadyBoundary =
    options?.retryPolicy?.retryOnReadyBoundary ??
    (command === "reactToUnity_xrOverlayGazeOff" ||
      command === "reactToUnity_setUploadButtonEnabled" ||
      command === "reactToUnity_loadViewR");
  return {
    maxAttempts,
    retryOnReadyBoundary: maxAttempts > 1 ? retryOnReadyBoundary : false,
  };
}

function buildRuntimeDedupeKey(args: {
  command: string;
  transactionId: string | null;
  options?: UnitySendOptions;
}): string {
  if (typeof args.options?.dedupeKey === "string" && args.options.dedupeKey.trim()) {
    return args.options.dedupeKey;
  }
  return `${args.command}::${args.transactionId ?? "global"}`;
}

function getRuntimeBridgeLogFields(): Record<string, unknown> {
  const snapshot = getUnityRuntimeBridgeSnapshot();
  return {
    readyState: snapshot.readyState,
    queueDepth: snapshot.queueDepth,
  };
}

function extractEventTransactionId(eventData: UnityMessageEvent): string | null {
  const parseCandidate = (candidate: unknown): Record<string, unknown> | null => {
    if (typeof candidate === "object" && candidate !== null) {
      return candidate as Record<string, unknown>;
    }
    if (typeof candidate === "string" && candidate.trim()) {
      try {
        const parsed = JSON.parse(candidate);
        return typeof parsed === "object" && parsed !== null
          ? (parsed as Record<string, unknown>)
          : null;
      } catch {
        return null;
      }
    }
    return null;
  };
  const payload = parseCandidate(eventData.payload) ?? parseCandidate(eventData.data);
  if (!payload) return null;
  const transactionId = payload.transactionId;
  return typeof transactionId === "string" && transactionId.trim()
    ? transactionId
    : null;
}

function refreshRuntimeQueueDepth(): void {
  setUnityRuntimeQueueDepth(runtimeCommandQueue.length, "UnityBridge:queue");
}

function clearRuntimeQueuedCommands(
  reason: "reset" | "cleanup" | "stale_transaction",
): void {
  // stale_transaction 사유로 큐를 비울 때는 exempt 커맨드(예: xrOverlayGazeOff)는 보존한다.
  // 이 커맨드들은 자체 트랜잭션 생명주기를 가지므로 space switch 등 외부 트랜잭션
  // 전환에 의해 purge되면 안 된다.
  const preserved: RuntimeQueuedCommand[] = [];
  while (runtimeCommandQueue.length > 0) {
    const queued = runtimeCommandQueue.shift();
    if (!queued) continue;

    if (
      reason === "stale_transaction" &&
      RUNTIME_STALE_CHECK_EXEMPT_COMMANDS.has(queued.command)
    ) {
      preserved.push(queued);
      continue;
    }

    runtimeQueuedDedupeKeys.delete(queued.dedupeKey);
    if (reason === "stale_transaction") {
      logEditorCorrelation("stale_ignored", {
        source: "UnityBridge",
        phase: "command_queue",
      }, {
        transactionId: queued.transactionId,
        command: queued.command,
        dedupeKey: queued.dedupeKey,
        isStale: true,
        retryCount: queued.attempts,
        ackReceived: null,
        ...getRuntimeBridgeLogFields(),
      });
    }
    queued.resolve();
  }

  if (preserved.length > 0) {
    runtimeCommandQueue.push(...preserved);
  } else {
    runtimeQueuedDedupeKeys.clear();
  }
  refreshRuntimeQueueDepth();
}

function clearRuntimeDeliveredDedupeKeys(): void {
  runtimeDeliveredDedupeKeys.clear();
}

function shouldSkipStaleTransactionCheck(
  transactionId: string | null,
  options?: Pick<UnitySendOptions, "skipStaleTransactionCheck">,
): boolean {
  return (
    options?.skipStaleTransactionCheck === true ||
    isEditorImportReloadCorrelationTransaction(transactionId)
  );
}

function isStaleQueuedCommandTransaction(
  transactionId: string | null,
  options?: Pick<UnitySendOptions, "skipStaleTransactionCheck">,
): boolean {
  if (!transactionId || shouldSkipStaleTransactionCheck(transactionId, options)) {
    return false;
  }
  const activeTransactionId = getUnityRuntimeBridgeSnapshot().transactionId;
  if (!activeTransactionId) return false;
  return activeTransactionId !== transactionId;
}

async function flushRuntimeCommandQueue(trigger: string): Promise<void> {
  if (runtimeQueueFlushInProgress || runtimeCommandQueue.length === 0) {
    return;
  }

  runtimeQueueFlushInProgress = true;
  try {
    for (let i = 0; i < runtimeCommandQueue.length; ) {
      const queued = runtimeCommandQueue[i];
      const snapshot = getUnityRuntimeBridgeSnapshot();

      if (
        !RUNTIME_STALE_CHECK_EXEMPT_COMMANDS.has(queued.command) &&
        isStaleQueuedCommandTransaction(queued.transactionId, queued.options)
      ) {
        runtimeQueuedDedupeKeys.delete(queued.dedupeKey);
        runtimeCommandQueue.splice(i, 1);
        queued.resolve();
        logEditorCorrelation("stale_ignored", {
          source: "UnityBridge",
          phase: "command_queue",
        }, {
          transactionId: queued.transactionId,
          command: queued.command,
          dedupeKey: queued.dedupeKey,
          isStale: true,
          retryCount: queued.attempts,
          ackReceived: null,
          trigger,
          readyState: snapshot.readyState,
          queueDepth: snapshot.queueDepth,
        });
        continue;
      }

      if (!isUnityRuntimeReadyAtLeast(queued.requiredReadyState)) {
        i += 1;
        continue;
      }

      if (
        typeof queued.waitUntilBoundarySeq === "number" &&
        snapshot.readyBoundarySeq <= queued.waitUntilBoundarySeq
      ) {
        i += 1;
        continue;
      }

      queued.attempts += 1;
      try {
        await sendToUnityWithResponse(queued.command, queued.payload, {
          ...queued.options,
          __skipRuntimeGate: true,
        });
        if (
          queued.options.retryPolicy?.retryOnReadyBoundary === true &&
          queued.attempts < queued.maxAttempts
        ) {
          queued.waitUntilBoundarySeq = snapshot.readyBoundarySeq;
          i += 1;
          continue;
        }
        runtimeQueuedDedupeKeys.delete(queued.dedupeKey);
        runtimeCommandQueue.splice(i, 1);
        queued.resolve();
      } catch (error) {
        if (queued.attempts < queued.maxAttempts) {
          queued.waitUntilBoundarySeq = snapshot.readyBoundarySeq;
          i += 1;
          continue;
        }
        runtimeQueuedDedupeKeys.delete(queued.dedupeKey);
        runtimeCommandQueue.splice(i, 1);
        queued.reject(error);
      }
    }
  } finally {
    refreshRuntimeQueueDepth();
    runtimeQueueFlushInProgress = false;
  }
}

function enqueueRuntimeCommand(queued: RuntimeQueuedCommand): Promise<void> {
  if (runtimeQueuedDedupeKeys.has(queued.dedupeKey)) {
    return Promise.resolve();
  }
  runtimeQueuedDedupeKeys.add(queued.dedupeKey);
  runtimeCommandQueue.push(queued);
  refreshRuntimeQueueDepth();
  return Promise.resolve();
}

// 메시지 이벤트 핸들러 함수
function handleUnityMessage(event: MessageEvent): void {
  const eventData = normalizeUnityIframeMessageData(event.data) as UnityMessageEvent | null;

  // Unity가 로드되었을 때 메시지 확인
  if (eventData && eventData.type === "unityToReact_unityLoaded") {
    // 로드 메시지를 받으면 Unity가 준비된 것으로 간주
    setUnityReady(true);
    const transactionId =
      extractEventTransactionId(eventData) ?? getActiveSpaceSwitchTransactionId();
    setUnityRuntimeReadyState({
      readyState: "bridge_ready",
      transactionId,
      source: "UnityBridge:unity_loaded",
    });
    void flushRuntimeCommandQueue("unity_loaded");
  }

  // Unity가 준비되었을 때 메시지 확인
  if (eventData && eventData.type === "unityToReact_unityReady") {
    setUnityReady(true);
    const transactionId =
      extractEventTransactionId(eventData) ?? getActiveSpaceSwitchTransactionId();
    setUnityRuntimeReadyState({
      readyState: "handlers_ready",
      transactionId,
      source: "UnityBridge:unity_ready",
    });
    void flushRuntimeCommandQueue("unity_ready");
  }

  // Unity가 보낸 ViewRData 수신 및 로깅
  if (eventData && eventData.type === "unityToReact_viewRData") {
    const viewRData =
      eventData.payload ?? eventData.viewRData ?? eventData.data;
    try {
      // 구독자에게 브로드캐스트
      const evt = new CustomEvent("viewr:viewRData", { detail: viewRData });
      window.dispatchEvent(evt);
    } catch (e) {
      // CustomEvent 미지원 환경 대비
    }
  }
}

export function subscribeToUnityBridgeMessages(
  listener: (event: MessageEvent) => void,
): () => void {
  window.addEventListener("message", listener);
  return () => {
    window.removeEventListener("message", listener);
  };
}

// 초기화 함수
export function initializeUnityBridge(): void {
  if (isInitialized) {
    return;
  }

  if (isDev) console.log("UnityBridge 초기화");
  let lastRevision = getEditorImportStateSnapshot().revision;
  unsubscribeImportProjection?.();
  unsubscribeImportProjection = useUploadStore.subscribe(() => {
    const revision = getEditorImportStateSnapshot().revision;
    if (revision !== lastRevision) {
      lastRevision = revision;
      syncEditorImportProjection();
    }
  });

  // 이벤트 리스너 등록
  messageEventListener = handleUnityMessage;
  window.addEventListener("message", messageEventListener);
  runtimeReadyStateListener = () => {
    void flushRuntimeCommandQueue("runtime_ready_state_changed");
  };
  window.addEventListener(
    UNITY_RUNTIME_READY_STATE_EVENT,
    runtimeReadyStateListener,
  );

  isInitialized = true;
}

// 정리 함수
export function cleanupUnityBridge(): void {
  unsubscribeImportProjection?.();
  unsubscribeImportProjection = null;
  // 이벤트 리스너 제거
  if (messageEventListener) {
    window.removeEventListener("message", messageEventListener);
    messageEventListener = null;
  }
  if (runtimeReadyStateListener) {
    window.removeEventListener(
      UNITY_RUNTIME_READY_STATE_EVENT,
      runtimeReadyStateListener,
    );
    runtimeReadyStateListener = null;
  }

  // 인터벌 정리
  if (unityCheckInterval) {
    clearInterval(unityCheckInterval);
    unityCheckInterval = null;
  }

  // 상태 초기화
  resetUnityState();
  unityMessageQueue.clear();
  pendingUnityCheck = null;
  clearRuntimeQueuedCommands("cleanup");
  setUnityRuntimeReadyState({
    readyState: "booting",
    transactionId: null,
    source: "UnityBridge:cleanup",
    force: true,
  });
  isInitialized = false;
}

/**
 * Unity WebGL 인스턴스에 Quit 요청 전송 (EDITOR 언마운트 시 메모리 해제)
 * iframe 제거 전 호출하여 UnityInstance.Quit()를 실행합니다.
 */
export function sendUnityQuitRequest(): void {
  const iframe = getUnityInstance();
  if (iframe?.contentWindow) {
    try {
      iframe.contentWindow.postMessage(
        { type: SYSTEM_MESSAGE_TYPES.REACT_TO_UNITY_QUIT },
        "*"
      );
    } catch (err) {
      if (isDev) console.warn("Unity Quit 요청 전송 실패:", err);
    }
  }
}

// SPA 라우트 전환 시 가벼운 상태 초기화 (이벤트 리스너는 유지)
export function resetUnityBridgeState(): void {
  // 인터벌/대기 promise 정리
  if (unityCheckInterval) {
    clearInterval(unityCheckInterval);
    unityCheckInterval = null;
  }
  pendingUnityCheck = null;

  // 상태 리셋
  resetUnityState();

  // 메시지 큐 비우기
  unityMessageQueue.clear();
  clearRuntimeQueuedCommands("reset");
  clearRuntimeDeliveredDedupeKeys();
  setUnityRuntimeReadyState({
    readyState: "booting",
    transactionId: null,
    source: "UnityBridge:reset",
    force: true,
  });
}

export function bootstrapUnityRuntimeTransaction(
  transactionId: string | null,
  source = "UnityBridge:bootstrap_transaction",
  options?: { preserveReadyState?: boolean },
): void {
  clearRuntimeQueuedCommands("stale_transaction");
  clearRuntimeDeliveredDedupeKeys();
  setUnityRuntimeReadyState({
    readyState: options?.preserveReadyState
      ? getUnityRuntimeBridgeSnapshot().readyState
      : "booting",
    transactionId: transactionId ?? null,
    source,
    force: true,
  });
}

// iframe 강제 재로드 함수
export async function forceIframeReload(): Promise<boolean> {
  const iframe = getUnityInstance();
  if (iframe) {
    try {
      // iframe src를 다시 설정하여 강제 재로드
      const currentSrc = iframe.src;
      iframe.src = "";
      await new Promise((resolve) => setTimeout(resolve, 100));
      iframe.src = currentSrc;
      return true;
    } catch (error) {
      console.error("❌ iframe 재로드 실패:", error);
      return false;
    }
  }
  return false;
}

// Unity가 준비될 때까지 대기 (기본 30초 타임아웃)
export function waitForUnity(timeout = 30000): Promise<HTMLIFrameElement> {
  // 이미 대기 중인 Promise가 있으면 재사용 (중복 체크 방지)
  if (pendingUnityCheck) {
    return pendingUnityCheck;
  }

  pendingUnityCheck = new Promise<HTMLIFrameElement>((resolve, reject) => {
    // 먼저 iframe을 찾아보기
    let iframe = getUnityInstance();

    // iframe이 이미 있고 준비되어 있으면 즉시 반환
    if (iframe && isUnityReady()) {
      pendingUnityCheck = null;
      resolve(iframe);
      return;
    }

    const startTime = Date.now();

    // 이미 인터벌이 실행 중이면 중복 생성 방지
    if (unityCheckInterval) {
      // 기존 인터벌이 있으면 그대로 사용
    } else {
      unityCheckInterval = setInterval(() => {
        // 매번 iframe을 다시 찾기 (DOM에 추가되었을 수 있음)
        const currentIframe = getUnityInstance();

        // iframe이 없으면 다음 체크에서 다시 시도
        if (!currentIframe) {
          const elapsed = Date.now() - startTime;
          // 타임아웃 전까지는 계속 재시도
          if (elapsed > timeout) {
            clearInterval(unityCheckInterval!);
            unityCheckInterval = null;
            console.error(
              `❌ Unity iframe을 찾을 수 없음 (${timeout}ms 타임아웃)`
            );
            pendingUnityCheck = null;
            reject(
              new Error(
                `Unity iframe not found in DOM after ${timeout}ms timeout. Check console for diagnosis.`
              )
            );
          }
          return;
        }

        // iframe이 있고 contentWindow가 있으면 강제로 준비 상태로 설정
        if (currentIframe && currentIframe.contentWindow) {
          setUnityReady(true);
        }

        if (isUnityReady() && currentIframe) {
          clearInterval(unityCheckInterval!);
          unityCheckInterval = null;
          pendingUnityCheck = null;
          resolve(currentIframe);
        } else if (Date.now() - startTime > timeout) {
          clearInterval(unityCheckInterval!);
          unityCheckInterval = null;

          // 타임아웃 시 상세한 디버깅 정보 제공
          console.error(`❌ Unity 준비 타임아웃 (${timeout}ms)`);
          const diagnosis = diagnoseUnityConnection();
          console.error("🔍 타임아웃 시 Unity 진단:", diagnosis);

          // iframe이 있고 contentWindow가 있으면 강제로 성공 처리
          if (currentIframe && currentIframe.contentWindow) {
            setUnityReady(true);
            pendingUnityCheck = null;
            resolve(currentIframe);
            return;
          }

          pendingUnityCheck = null;
          reject(
            new Error(
              `Unity not ready after ${timeout}ms timeout. Check console for diagnosis.`
            )
          );
        }
      }, 100);
    }
  });

  return pendingUnityCheck;
}

// sendToUnitySafe는 모듈에서 export (아래 re-export 섹션 참조)

/**
 * Sends ViewR data to Unity using the INSERT_VIEWR_DATA command
 * @param viewrData - The ViewR data object containing platform files and metadata
 * @returns Promise that resolves if the message is successfully sent
 */
export async function sendViewRDataToUnity(
  viewrData: Record<string, unknown>
): Promise<void> {
  const requestId = Math.floor(Math.random() * 10000);
  if (isDev) console.log(`[${requestId}] ViewR 데이터 전송 시작`);

  try {
    await sendToUnityWithResponse("reactToUnity_insertViewRData", viewrData);
    if (isDev) console.log(`[${requestId}] ViewR 데이터 전송 완료`);
  } catch (error) {
    console.error(`❌ [${requestId}] ViewR 데이터 전송 실패:`, error);
    throw error;
  }
}

// 현재 페이지가 EDITOR 페이지인지 확인하는 헬퍼 함수
function isEditorPage(): boolean {
  // URL 경로 확인
  const path = window.location.pathname;
  if (path.includes("/editor")) {
    return true;
  }

  // Unity iframe이 DOM에 있는지 확인 (EDITOR 페이지에만 있음)
  const unityIframe =
    document.querySelector('iframe[id="unity-iframe"]') ||
    document.querySelector('iframe[title="Unity WebGL Application"]') ||
    document.querySelector('iframe[src*="unity"]');

  return !!unityIframe;
}

/**
 * Sends a command and payload to the Unity iframe using postMessage.
 * Waits for the Unity instance to be ready before sending the message.
 *
 * @param command - The command to send to Unity.
 * @param payload - Optional payload to accompany the command.
 * @returns Promise that resolves if the message is successfully sent, rejects if an error occurs.
 */
export async function sendToUnityWithResponse(
  command: string,
  payload: Record<string, unknown> = {},
  options: UnitySendOptions = {},
): Promise<void> {
  const transactionId = getCommandTransactionId(payload, options);
  if (
    typeof options.transactionId !== "undefined" &&
    options.transactionId === null &&
    transactionId
  ) {
    setUnityRuntimeReadyState({
      readyState: "booting",
      transactionId: null,
      source: "UnityBridge:explicit_transaction_reset",
      force: true,
    });
  }
  if (
    typeof options.transactionId === "string" &&
    options.transactionId.trim() &&
    options.requiresReadyState === "booting"
  ) {
    setUnityRuntimeReadyState({
      readyState: "booting",
      transactionId: options.transactionId,
      source: "UnityBridge:transaction_bootstrap",
      force: true,
    });
    clearRuntimeQueuedCommands("stale_transaction");
    clearRuntimeDeliveredDedupeKeys();
  }

  const requiredReadyState = getRequiredReadyState(command, options);
  const retryPolicy = getRetryPolicy(command, options);
  const dedupeKey = buildRuntimeDedupeKey({
    command,
    transactionId,
    options,
  });
  const shouldDedupeDelivered =
    RUNTIME_DELIVERED_DEDUPE_COMMANDS.has(command) &&
    typeof options.dedupeKey === "string" &&
    options.dedupeKey.trim().length > 0;
  if (
    options.__skipRuntimeGate !== true &&
    shouldDedupeDelivered &&
    runtimeDeliveredDedupeKeys.has(dedupeKey)
  ) {
    return;
  }
  const isStale =
    !RUNTIME_STALE_CHECK_EXEMPT_COMMANDS.has(command) &&
    isStaleQueuedCommandTransaction(transactionId, options);

  if (isStale && options.__skipRuntimeGate !== true) {
    logEditorCorrelation(
      "stale_ignored",
      {
        source: "UnityBridge",
        phase: "command_dispatch",
      },
      {
        transactionId,
        command,
        dedupeKey,
        isStale: true,
        retryCount: 0,
        ackReceived: null,
        ...getRuntimeBridgeLogFields(),
      },
    );
    return;
  }

  if (
    options.__skipRuntimeGate !== true &&
    !isUnityRuntimeReadyAtLeast(requiredReadyState)
  ) {
    return new Promise<void>((resolve, reject) => {
      void enqueueRuntimeCommand({
        command,
        payload,
        options: {
          ...options,
          transactionId,
          dedupeKey,
          requiresReadyState: requiredReadyState,
          retryPolicy,
        },
        requiredReadyState,
        dedupeKey,
        transactionId,
        maxAttempts: retryPolicy.maxAttempts,
        attempts: 0,
        waitUntilBoundarySeq: null,
        resolve,
        reject,
      });
    });
  }

  // 디버깅용 요청 ID 생성
  const requestId = Math.floor(Math.random() * 10000);
  if (isDev) console.log(`[${requestId}] Unity 요청: ${command}`);

  // EDITOR 페이지가 아닌 경우 빠른 실패
  if (!isEditorPage()) {
    throw new Error("Unity iframe not available - not on EDITOR page");
  }

  try {
    let iframe = getUnityInstance();

    // 먼저 iframe이 존재하고 contentWindow가 있는지 확인
    if (iframe && iframe.contentWindow) {
      setUnityReady(true);
    } else if (iframe && !iframe.contentWindow) {
      // iframe은 있지만 contentWindow가 없는 경우 - 강화된 로딩 대기

      // 1단계: 빠른 재시도 (1초)
      let attempts = 0;
      const quickAttempts = 20; // 1초 대기 (50ms * 20)

      while (attempts < quickAttempts && !iframe.contentWindow) {
        await new Promise((resolve) => setTimeout(resolve, 50));
        attempts++;

        if (iframe.contentWindow) break;
      }

      // 2단계: 여전히 contentWindow가 없으면 iframe 재로드 시도
      if (!iframe.contentWindow) {
        const reloadSuccess = await forceIframeReload();

        if (reloadSuccess) {
          // 재로드 후 다시 대기
          attempts = 0;
          const reloadAttempts = 40; // 2초 대기 (50ms * 40)

          while (attempts < reloadAttempts && !iframe.contentWindow) {
            await new Promise((resolve) => setTimeout(resolve, 50));
            attempts++;

            if (iframe.contentWindow) break;
          }
        }
      }

      // 3단계: contentWindow가 있으면 Unity 준비 상태로 설정
      if (iframe.contentWindow) {
        setUnityReady(true);
      } else {
        // 4단계: 모든 시도 실패 시 Unity 준비 대기로 전환
        iframe = await waitForUnity(30000);
      }
    } else {
      // iframe이 없으면 정상적인 대기 프로세스
      iframe = await waitForUnity(30000);
    }

    if (!iframe) {
      throw new Error("Unity iframe not found");
    }

    // iframe이 로드되었는지 확인 (더 강력한 재시도 로직)
    if (!iframe.contentWindow) {
      if (isDev) console.warn(`[${requestId}] iframe contentWindow 없음, 재시도`);

      // 외부 도메인 iframe인지 확인
      let isExternalDomain = false;
      if (iframe.src) {
        try {
          const iframeUrl = new URL(iframe.src, window.location.origin);
          const currentUrl = new URL(window.location.href);
          isExternalDomain = iframeUrl.hostname !== currentUrl.hostname;
        } catch (_e) {
          /* URL 파싱 오류 시 기본 재시도 진행 */
        }
      }

      // 외부 도메인이 아닌 경우에만 contentWindow 재시도
      if (!isExternalDomain) {
        // 최대 10초간 재시도 (20번 x 500ms)
        for (let attempt = 1; attempt <= 20; attempt++) {
          await new Promise((resolve) => setTimeout(resolve, 500));

          // iframe을 다시 찾기 (DOM이 변경되었을 수 있음)
          iframe = getUnityInstance();

          if (iframe && iframe.contentWindow) break;

          if (attempt === 20) {
            if (isDev) console.warn(`[${requestId}] iframe contentWindow 준비 실패`);
            break;
          }
        }
      }
    }

    options.assertCurrent?.();
    const message = serializeEditorImportCommand(command, payload);

    if (isDev && command === "reactToUnity_figmaPlaneTransformUpdate") {
      console.log("[FigmaTransformVerify] React→Unity figmaPlaneTransformUpdate", {
        position: payload.position,
        rotation: payload.rotation,
        vseIndex: payload.vseIndex,
        hmiIndex: payload.hmiIndex,
      });
    }

    try {
      // 메시지 유효성 검증
      if (!message || message.length === 0) {
        throw new Error("Empty message content");
      }

      // JSON 파싱 테스트
      try {
        JSON.parse(message);
      } catch (jsonError) {
        console.error(`❌ [${requestId}] 잘못된 JSON 형식:`, jsonError);
        throw new Error(
          `Invalid JSON format: ${
            jsonError instanceof Error ? jsonError.message : "Unknown error"
          }`
        );
      }

      // iframe으로 postMessage 전송
      const messageData = {
        type: "reactToUnity_command",
        gameObject: "ViewRReactBridge",
        method: "ReceiveFromReact",
        message: message,
      };

      // contentWindow가 있는 경우 직접 전송
      if (iframe.contentWindow) {
        iframe.contentWindow.postMessage(messageData, "*");
      } else if (iframe.src) {
        // contentWindow가 없지만 src가 있는 경우 - window.postMessage로 시도
        window.postMessage(messageData, "*");
      } else {
        throw new Error("iframe contentWindow not available and no src");
      }

      // 백업으로 여러 타입의 메시지 전송
      const backupMessages = [
        { ...messageData, type: "REACT_TO_UNITY" },
        { ...messageData, type: "reactToUnity" },
        { event: "reactToUnity_command", data: messageData },
      ];

      backupMessages.forEach((backupMsg) => {
        try {
          if (iframe.contentWindow) {
            iframe.contentWindow.postMessage(backupMsg, "*");
          } else if (iframe.src) {
            window.postMessage(backupMsg, "*");
          }
        } catch (backupError) {
          if (isDev) console.warn(`[${requestId}] 백업 메시지 전송 실패:`, backupError);
        }
      });

      // Phase 1.3: 직렬화 결과 참조 해제로 GC 유도 (대용량 JSON 메모리 해제)
      (messageData as { message?: string }).message = undefined;
      backupMessages.forEach((m) => {
        if (m && "message" in m) (m as { message?: string }).message = undefined;
        const d = m && "data" in m ? (m as { data?: { message?: string } }).data : undefined;
        if (d && typeof d === "object" && "message" in d) d.message = undefined;
      });

      if (isDev) console.log(`[${requestId}] Unity 메시지 전송 완료: ${command}`);
      if (shouldDedupeDelivered) {
        runtimeDeliveredDedupeKeys.add(dedupeKey);
      }
      return Promise.resolve();
    } catch (postMessageError) {
      console.error(
        `❌ [${requestId}] postMessage 전송 실패:`,
        postMessageError
      );
      throw new Error(
        `postMessage failed: ${
          postMessageError instanceof Error
            ? postMessageError.message
            : "Unknown error"
        }`
      );
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    console.error("❌ Unity 메시지 전송 실패:", errorMessage);

    // 진단 정보 출력
    const diagnosis = diagnoseUnityConnection();
    console.error("🔍 Unity 통신 실패 시 진단:", diagnosis);

    // iframe이 있고 contentWindow가 있으면 재시도
    const iframe = getUnityInstance();
    if (iframe && iframe.contentWindow) {
      try {
        options.assertCurrent?.();
        // 간단한 postMessage로 재시도
        iframe.contentWindow.postMessage(
          {
            type: "reactToUnity_command",
            gameObject: "ViewRReactBridge",
            method: "ReceiveFromReact",
            message: serializeEditorImportCommand(command, payload),
          },
          "*"
        );
        return Promise.resolve();
      } catch (retryError) {
        console.error("❌ 재시도 실패:", retryError);
      }
    }

    return Promise.reject(error);
  }
}

/**
 * XR transform 같은 고빈도 경로용 빠른 전송.
 * Unity index.html이 직접 처리하는 REACT_TO_UNITY 타입만 한 번 전송합니다.
 */
export async function sendToUnityNoFanout(
  command: string,
  payload: Record<string, unknown> = {}
): Promise<void> {
  const requestId = Math.floor(Math.random() * 10000);
  if (isDev) console.log(`[${requestId}] Unity 빠른 요청: ${command}`);

  if (!isEditorPage()) {
    throw new Error("Unity iframe not available - not on EDITOR page");
  }

  const iframe = await waitForUnity(30000);
  if (!iframe?.contentWindow) {
    throw new Error("Unity iframe contentWindow not available");
  }

  const message = serializeEditorImportCommand(command, payload);
  const messageData = {
    type: "REACT_TO_UNITY",
    gameObject: "ViewRReactBridge",
    method: "ReceiveFromReact",
    message,
  };

  iframe.contentWindow.postMessage(messageData, "*");
  if (isDev) console.log(`[${requestId}] Unity 빠른 메시지 전송 완료: ${command}`);
}

interface UnityDiagnosis {
  timestamp: string;
  unityReadyState: boolean;
  unityLoadedCheck: boolean;
  isInitialized: boolean;
  messageQueueLength: number;
  hasEventListener: boolean;
  hasCheckInterval: boolean;
  hasPendingCheck: boolean;
  iframe: {
    found: boolean;
    src: string;
    title: string;
    hasContentWindow: boolean;
    readyState: string;
  };
  allIframes: Array<{
    index: number;
    src: string;
    title: string;
    hasContentWindow: boolean;
  }>;
  isReady: boolean;
}

// Unity 통신 상태 진단 함수
export function diagnoseUnityConnection(): UnityDiagnosis {
  const iframe = getUnityInstance();

  const diagnosis: UnityDiagnosis = {
    timestamp: new Date().toISOString(),
    unityReadyState: isUnityReady(),
    unityLoadedCheck: isUnityReady(),
    isInitialized,
    messageQueueLength: unityMessageQueue.size(),
    hasEventListener: !!messageEventListener,
    hasCheckInterval: !!unityCheckInterval,
    hasPendingCheck: !!pendingUnityCheck,
    iframe: {
      found: !!iframe,
      src: iframe?.src || "N/A",
      title: iframe?.title || "N/A",
      hasContentWindow: !!iframe?.contentWindow,
      readyState: iframe?.contentDocument?.readyState || "N/A",
    },
    allIframes: [],
    isReady: isUnityReady(),
  };

  // DOM에서 모든 iframe 찾기
  const allIframes = document.querySelectorAll("iframe");
  diagnosis.allIframes = Array.from(allIframes).map((iframe, index) => ({
    index,
    src: iframe.src,
    title: iframe.title,
    hasContentWindow: !!iframe.contentWindow,
  }));

  if (isDev) console.log("Unity 연결 진단:", diagnosis);
  return diagnosis;
}

// Unity 연결 강제 재시도 함수
export async function forceUnityReconnect(): Promise<boolean> {
  // 현재 상태 초기화
  resetUnityState();

  // iframe 다시 찾기
  const iframe = getUnityInstance();
  if (iframe && iframe.contentWindow) {
    // Unity에 준비 확인 메시지 전송
    try {
      iframe.contentWindow.postMessage(
        {
          type: "reactToUnity_ping",
          timestamp: Date.now(),
        },
        "*"
      );
    } catch (error) {
      console.error("❌ Unity ping 전송 실패:", error);
    }

    // 잠시 대기 후 강제로 준비 상태 설정
    setTimeout(() => {
      if (!isUnityReady()) setUnityReady(true);
    }, 2000);

    return true;
  } else {
    console.error("❌ Unity iframe을 찾을 수 없어 재연결 실패");
    return false;
  }
}

// 모듈에서 export한 함수들 re-export (다른 파일에서 사용할 수 있도록)
export {
  isUnityReady,
  setUnityReady,
  getUnityInstance,
  resetUnityState,
} from "./modules/unityReadyCheck";
export {
  processMessageQueue,
  sendToUnitySafe,
  createThrottledSend,
  type SendToUnityWithPayloadFn,
} from "./modules/unityMessageSender";

// 자동 초기화
initializeUnityBridge();

// 페이지 언로드 시 정리
window.addEventListener("beforeunload", cleanupUnityBridge);

// BFCache 대응: 페이지 숨김 시 정리, 다시 보여질 때 재초기화
// Retainer: 부모의 SendMessageToReact/onUnityMessage 가 iframe(Window@2076003) 참조 → delete 로 완전 제거
window.addEventListener("pagehide", () => {
  if (isDev) {
    console.log("[UnityBridge] pagehide -> cleanupUnityBridge()");
  }
  try {
    if ("onUnityMessage" in window) delete (window as unknown as Record<string, unknown>).onUnityMessage;
    if ("SendMessageToReact" in window) delete (window as unknown as Record<string, unknown>).SendMessageToReact;
  } catch (_) {
    /* ignore */
  }
  cleanupUnityBridge();
});

window.addEventListener("pageshow", (e: PageTransitionEvent) => {
  // 캐시에서 복원된 경우 상태가 꼬일 수 있으므로 강제 재초기화
  if (isDev) {
    console.log("[UnityBridge] pageshow -> reset/init", {
      persisted: e.persisted,
    });
  }
  resetUnityState();
  initializeUnityBridge();
});

// Window 인터페이스 확장 (개발 환경에서만)
declare global {
  interface Window {
    UnityBridge?: {
      diagnose: () => UnityDiagnosis;
      forceReconnect: () => Promise<boolean>;
      isReady: () => boolean;
      getState: () => {
        unityReadyState: boolean;
        unityLoadedCheck: boolean;
        queueLength: number;
      };
      cleanup: () => void;
      reinitialize: () => void;
      forceIframeReload: () => Promise<boolean>;
      testContentWindow: () => Window | null;
    };
    __viewr_currentSpaceIndex?: number;
  }
}

// 개발용 유틸리티 함수들을 전역에 노출 (개발 환경에서만)
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  window.UnityBridge = {
    diagnose: diagnoseUnityConnection,
    forceReconnect: forceUnityReconnect,
    isReady: isUnityReady,
    getState: () => ({
      unityReadyState: isUnityReady(),
      unityLoadedCheck: isUnityReady(),
      queueLength: unityMessageQueue.size(),
    }),
    cleanup: cleanupUnityBridge,
    reinitialize: () => {
      cleanupUnityBridge();
      initializeUnityBridge();
    },
    forceIframeReload: forceIframeReload,
    testContentWindow: () => {
      const iframe = getUnityInstance();
      if (iframe) {
        let isExternalDomain = false;
        if (iframe.src) {
          try {
            const iframeUrl = new URL(iframe.src, window.location.origin);
            const currentUrl = new URL(window.location.href);
            isExternalDomain = iframeUrl.hostname !== currentUrl.hostname;
          } catch (_e) {
            /* ignore */
          }
        }

        if (isDev) console.log("iframe 테스트:", {
          src: iframe.src,
          contentWindow: !!iframe.contentWindow,
          readyState: iframe.contentDocument?.readyState || "N/A",
          loading: iframe.loading,
          isExternalDomain,
          currentDomain: window.location.hostname,
          iframeDomain: iframe.src
            ? new URL(iframe.src, window.location.origin).hostname
            : "N/A",
        });
        return iframe.contentWindow;
      }
      return null;
    },
  };
}
